/**
 * t355u: where an assistant source (or an inline [n], or a link in an answer) opens on a phone, web or app. The API
 * cites desktop paths (they stay right on a computer); a few of those are desktop-only screens, so they are swapped for
 * the phone screen that shows the same thing. Everything else is already a phone path and passes through unchanged.
 *
 * - Schedule (/workforce/schedule?date&loc&emp&shift): my own shift opens its shift detail; anyone else's opens the
 *   phone Rota on that day and building, filtered to that person, with the shift open when there is one.
 * - My shifts for someone else (staff: /workforce/my-shifts?tab=team&date&loc&emp) opens My shifts › Team that week.
 * - Who's working (/workforce/timesheets?tab=team&date) opens the Rota on that day.
 * - A person's time off (/settings/people/users/<id>?tab=timeoff) opens their Time off; other user settings tabs and the
 *   People directory (/settings/people/users) open the person sheet and People.
 * - Leave (/calendar?date) opens the calendar's Day view; the team's time off (/workforce/time-off?tab=team) opens
 *   Approvals › Time off.
 */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function qs(entries: [string, string | null | undefined][]): string {
  const q = new URLSearchParams();
  for (const [k, v] of entries) if (v) q.set(k, v);
  const s = q.toString();
  return s ? `?${s}` : '';
}

export function phoneSourceLink(link: string, selfId?: string | null): string {
  const raw = link.replace(/^https?:\/\/[^/]+/, '');
  const hashAt = raw.indexOf('#');
  const noHash = hashAt >= 0 ? raw.slice(0, hashAt) : raw;
  const qAt = noHash.indexOf('?');
  const path = (qAt >= 0 ? noHash.slice(0, qAt) : noHash).replace(/\/+$/, '') || '/';
  const q = new URLSearchParams(qAt >= 0 ? noHash.slice(qAt + 1) : '');
  const id = (k: string) => { const v = q.get(k); return v && UUID.test(v) ? v : null; };
  const date = /^\d{4}-\d{2}-\d{2}$/.test(q.get('date') ?? '') ? q.get('date') : null;
  const shift = id('shift'), emp = id('emp'), loc = id('loc');
  const mine = Boolean(selfId) && emp === selfId;

  if (path === '/workforce/schedule') {
    if (shift && mine) return `/workforce/my-shifts/${shift}`;
    return `/rota${qs([['view', 'rota'], ['date', date], ['building', loc], ['person', emp], ['shift', shift]])}`;
  }
  if (path === '/workforce/my-shifts') {
    if (shift && (!emp || mine)) return `/workforce/my-shifts/${shift}`;
    if (q.get('tab') === 'team' || (emp && !mine)) return `/workforce/my-shifts${qs([['tab', 'team'], ['full', '1'], ['date', date], ['loc', loc], ['emp', mine ? null : emp]])}`;
    return raw;
  }
  if (path === '/workforce/timesheets' && q.get('tab') === 'team' && date) return `/rota${qs([['view', 'rota'], ['date', date]])}`;
  const user = /^\/settings\/people\/users\/([0-9a-f-]{36})$/i.exec(path)?.[1];
  if (user) return q.get('tab') === 'timeoff' ? `/workforce/time-off?user=${user}` : `/people/${user}`;
  if (path === '/settings/people/users') return '/people';
  if (path === '/calendar' && date && !q.get('view') && !q.get('event')) return `/calendar?view=day&date=${date}`;
  if (path === '/workforce/time-off' && q.get('tab') === 'team') return '/rota/approvals?kind=leave';
  return raw;
}

/** The person a phone source link opens as a sheet (web phone has no /people/<id> page), or null. */
export function sourcePersonId(phoneLink: string): string | null {
  return /^\/people\/([0-9a-f-]{36})\/?(?:[?#]|$)/i.exec(phoneLink)?.[1] ?? null;
}
